import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Car, Fuel, Gauge, Plus, ReceiptText, RefreshCw, ShieldAlert, WalletCards } from 'lucide-react';
import { useAuth } from '@/core/hooks';

interface FleetVehicle {
  id?: string;
  registration: string;
  make: string;
  model: string;
  year: number;
  status: string;
  ownershipType: string;
  assignedDriverId: string;
  assignedDriverName: string;
  branchId: string;
  fuelType: string;
  odometerMiles: number;
  insuranceExpiry: string;
  motExpiry: string;
  taxExpiry: string;
  notes: string;
}

interface FleetExpense {
  id?: string;
  vehicleId: string;
  vehicleRegistration: string;
  driverId: string;
  driverName: string;
  date: string;
  category: string;
  vendor: string;
  litres: number;
  unitPrice: number;
  amount: number;
  odometerMiles: number;
  paymentMethod: string;
  receiptNumber: string;
  notes: string;
  status: string;
  branchId: string;
}

interface Carer {
  id: string;
  fullName: string;
  branchId?: string;
}

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadCollection<T>(collection: string, token?: string | null): Promise<T[]> {
  const response = await fetch(`/api/v1/${collection}`, { headers: authHeaders(token) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Could not load ${collection}`);
  return Array.isArray(payload.data) ? payload.data : [];
}

async function saveRecord<T extends { id?: string }>(collection: string, record: T, token?: string | null) {
  const existing = Boolean(record.id);
  const response = await fetch(`/api/v1/${collection}${existing ? `/${encodeURIComponent(String(record.id))}` : ''}`, {
    method: existing ? 'PATCH' : 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(record),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Could not save ${collection}`);
  return payload.data;
}

const today = () => new Date().toISOString().slice(0, 10);
const money = (value: number) => Number(value || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
const expenseCategories = ['FUEL', 'CHARGING', 'MAINTENANCE', 'INSURANCE', 'MOT', 'TAX', 'TYRES', 'REPAIR', 'PARKING', 'CLEANING', 'OTHER'];

export const FleetManagement: React.FC = () => {
  const { token, user } = useAuth();
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [expenses, setExpenses] = useState<FleetExpense[]>([]);
  const [carers, setCarers] = useState<Carer[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [vehicleForm, setVehicleForm] = useState<FleetVehicle>({
    registration: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    status: 'ACTIVE',
    ownershipType: 'COMPANY_OWNED',
    assignedDriverId: '',
    assignedDriverName: '',
    branchId: 'branch-1',
    fuelType: 'Petrol',
    odometerMiles: 0,
    insuranceExpiry: '',
    motExpiry: '',
    taxExpiry: '',
    notes: '',
  });
  const [expenseForm, setExpenseForm] = useState<FleetExpense>({
    vehicleId: '',
    vehicleRegistration: '',
    driverId: '',
    driverName: '',
    date: today(),
    category: 'FUEL',
    vendor: '',
    litres: 0,
    unitPrice: 0,
    amount: 0,
    odometerMiles: 0,
    paymentMethod: 'Company card',
    receiptNumber: '',
    notes: '',
    status: 'PENDING_REVIEW',
    branchId: 'branch-1',
  });

  const isAdminLike = Boolean(user?.roles.some((role) => ['SUPER_ADMIN', 'ADMIN', 'REGISTERED_MANAGER', 'DEPUTY_MANAGER', 'MANAGER', 'ACCOUNTANT'].includes(role)));
  const canWriteFleet = Boolean(user?.permissions.includes('*') || user?.permissions.includes('fleet:write') || isAdminLike);
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selectedVehicleId) || vehicles[0] || null;
  const selectedExpenses = expenses.filter((expense) => expense.vehicleId === selectedVehicle?.id);

  const totals = useMemo(() => {
    const fuelExpenses = expenses.filter((expense) => ['FUEL', 'CHARGING'].includes(expense.category));
    const additionalExpenses = expenses.filter((expense) => !['FUEL', 'CHARGING'].includes(expense.category));
    const totalFuelCost = fuelExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const totalLitres = fuelExpenses.reduce((sum, expense) => sum + Number(expense.litres || 0), 0);
    const additionalCost = additionalExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const totalCost = totalFuelCost + additionalCost;
    const pendingReview = expenses.filter((expense) => expense.status !== 'APPROVED').length;
    const expiringSoon = vehicles.filter((vehicle) => {
      const limit = new Date(Date.now() + 45 * 86400000).toISOString().slice(0, 10);
      return [vehicle.insuranceExpiry, vehicle.motExpiry, vehicle.taxExpiry].some((date) => date && date <= limit);
    }).length;
    return { totalFuelCost, totalLitres, additionalCost, totalCost, pendingReview, expiringSoon };
  }, [expenses, vehicles]);

  const perVehicle = useMemo(() => vehicles.map((vehicle) => {
    const vehicleExpenses = expenses.filter((expense) => expense.vehicleId === vehicle.id);
    const fuel = vehicleExpenses.filter((expense) => ['FUEL', 'CHARGING'].includes(expense.category));
    const other = vehicleExpenses.filter((expense) => !['FUEL', 'CHARGING'].includes(expense.category));
    return {
      vehicle,
      fuelCost: fuel.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
      litres: fuel.reduce((sum, expense) => sum + Number(expense.litres || 0), 0),
      otherCost: other.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
      total: vehicleExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
    };
  }).sort((left, right) => right.total - left.total), [expenses, vehicles]);

  const load = async () => {
    setIsLoading(true);
    try {
      const [nextVehicles, nextExpenses, nextCarers] = await Promise.all([
        loadCollection<FleetVehicle>('fleetVehicles', token),
        loadCollection<FleetExpense>('fleetExpenses', token),
        loadCollection<Carer>('carers', token),
      ]);
      setVehicles(nextVehicles);
      setExpenses(nextExpenses);
      setCarers(nextCarers);
      setSelectedVehicleId((current) => nextVehicles.some((vehicle) => vehicle.id === current) ? current : nextVehicles[0]?.id || '');
      if (nextVehicles[0]) {
        setExpenseForm((current) => ({
          ...current,
          vehicleId: current.vehicleId || nextVehicles[0].id || '',
          vehicleRegistration: current.vehicleRegistration || nextVehicles[0].registration,
          branchId: current.branchId || nextVehicles[0].branchId,
        }));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load fleet data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [token]);

  const saveVehicle = async () => {
    if (!vehicleForm.registration.trim()) {
      toast.error('Vehicle registration is required');
      return;
    }
    setIsSaving(true);
    try {
      const driver = carers.find((carer) => carer.id === vehicleForm.assignedDriverId);
      const saved = await saveRecord<FleetVehicle>('fleetVehicles', {
        ...vehicleForm,
        assignedDriverName: driver?.fullName || vehicleForm.assignedDriverName,
        branchId: driver?.branchId || vehicleForm.branchId || 'branch-1',
      }, token);
      setVehicles((current) => [saved, ...current.filter((vehicle) => vehicle.id !== saved.id)]);
      setSelectedVehicleId(saved.id || '');
      setVehicleForm({ ...vehicleForm, id: undefined, registration: '', make: '', model: '', assignedDriverId: '', assignedDriverName: '', notes: '' });
      toast.success('Vehicle saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save vehicle');
    } finally {
      setIsSaving(false);
    }
  };

  const saveExpense = async () => {
    if (!expenseForm.vehicleId) {
      toast.error('Select a vehicle first');
      return;
    }
    if (!expenseForm.amount && !(expenseForm.litres && expenseForm.unitPrice)) {
      toast.error('Enter litres/unit price or total amount');
      return;
    }
    setIsSaving(true);
    try {
      const vehicle = vehicles.find((item) => item.id === expenseForm.vehicleId);
      const driver = carers.find((carer) => carer.id === expenseForm.driverId);
      const amount = expenseForm.amount || Number(expenseForm.litres || 0) * Number(expenseForm.unitPrice || 0);
      const saved = await saveRecord<FleetExpense>('fleetExpenses', {
        ...expenseForm,
        vehicleRegistration: vehicle?.registration || expenseForm.vehicleRegistration,
        driverName: driver?.fullName || expenseForm.driverName || user?.name || '',
        branchId: vehicle?.branchId || driver?.branchId || expenseForm.branchId || 'branch-1',
        amount,
      }, token);
      setExpenses((current) => [saved, ...current.filter((expense) => expense.id !== saved.id)]);
      setExpenseForm({
        ...expenseForm,
        id: undefined,
        vendor: '',
        litres: 0,
        unitPrice: 0,
        amount: 0,
        receiptNumber: '',
        notes: '',
        date: today(),
      });
      toast.success('Vehicle expense saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save expense');
    } finally {
      setIsSaving(false);
    }
  };

  const updateExpenseStatus = async (expense: FleetExpense, status: string) => {
    try {
      const saved = await saveRecord<FleetExpense>('fleetExpenses', { ...expense, status }, token);
      setExpenses((current) => current.map((item) => item.id === saved.id ? saved : item));
      toast.success('Expense status updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update expense');
    }
  };

  const selectVehicleForExpense = (vehicleId: string) => {
    const vehicle = vehicles.find((item) => item.id === vehicleId);
    setSelectedVehicleId(vehicleId);
    setExpenseForm((current) => ({
      ...current,
      vehicleId,
      vehicleRegistration: vehicle?.registration || '',
      branchId: vehicle?.branchId || current.branchId,
      odometerMiles: vehicle?.odometerMiles || current.odometerMiles,
    }));
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-lg bg-purple-100 text-purple-700">
              <Car className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-purple-800">Fleet and vehicle costs</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Company Vehicle Control</h1>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Track company cars, fuel litres, fuel value, charging, maintenance, insurance, MOT, tax and every vehicle-related expense by driver and vehicle.
              </p>
            </div>
          </div>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <MetricCard label="Vehicles" value={vehicles.length} icon={<Car className="h-5 w-5" />} />
        <MetricCard label="Fuel/charging" value={money(totals.totalFuelCost)} icon={<Fuel className="h-5 w-5" />} />
        <MetricCard label="Litres recorded" value={totals.totalLitres.toFixed(1)} icon={<Gauge className="h-5 w-5" />} />
        <MetricCard label="Other costs" value={money(totals.additionalCost)} icon={<ReceiptText className="h-5 w-5" />} />
        <MetricCard label="Total cost" value={money(totals.totalCost)} icon={<WalletCards className="h-5 w-5" />} />
        <MetricCard label="Alerts" value={totals.pendingReview + totals.expiringSoon} icon={<ShieldAlert className="h-5 w-5" />} alert />
      </section>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-600">Loading fleet data...</div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
          <div className="space-y-6">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-bold text-slate-950">Record fuel or vehicle expense</h2>
              <div className="mt-4 space-y-3">
                <label className="space-y-2 block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vehicle</span>
                  <select value={expenseForm.vehicleId} onChange={(event) => selectVehicleForExpense(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                    {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.registration} - {vehicle.make} {vehicle.model}</option>)}
                  </select>
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</span>
                    <select value={expenseForm.category} onChange={(event) => setExpenseForm({ ...expenseForm, category: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                      {expenseCategories.map((category) => <option key={category}>{category}</option>)}
                    </select>
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</span>
                    <input type="date" value={expenseForm.date} onChange={(event) => setExpenseForm({ ...expenseForm, date: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </label>
                </div>
                <label className="space-y-2 block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Driver / employee</span>
                  <select value={expenseForm.driverId} onChange={(event) => setExpenseForm({ ...expenseForm, driverId: event.target.value, driverName: carers.find((carer) => carer.id === event.target.value)?.fullName || '' })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="">Office / not assigned</option>
                    {carers.map((carer) => <option key={carer.id} value={carer.id}>{carer.fullName}</option>)}
                  </select>
                </label>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Litres</span>
                    <input type="number" value={expenseForm.litres} onChange={(event) => setExpenseForm({ ...expenseForm, litres: Number(event.target.value), amount: Number(event.target.value) * Number(expenseForm.unitPrice || 0) })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Price/litre</span>
                    <input type="number" value={expenseForm.unitPrice} onChange={(event) => setExpenseForm({ ...expenseForm, unitPrice: Number(event.target.value), amount: Number(expenseForm.litres || 0) * Number(event.target.value) })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amount</span>
                    <input type="number" value={expenseForm.amount} onChange={(event) => setExpenseForm({ ...expenseForm, amount: Number(event.target.value) })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </label>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <input placeholder="Vendor / garage / station" value={expenseForm.vendor} onChange={(event) => setExpenseForm({ ...expenseForm, vendor: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <input placeholder="Receipt number" value={expenseForm.receiptNumber} onChange={(event) => setExpenseForm({ ...expenseForm, receiptNumber: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <textarea placeholder="Notes" value={expenseForm.notes} onChange={(event) => setExpenseForm({ ...expenseForm, notes: event.target.value })} rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" onClick={() => void saveExpense()} disabled={!canWriteFleet || isSaving} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                  <Plus className="h-4 w-4" />
                  Save expense
                </button>
              </div>
            </section>

            {isAdminLike && (
              <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-xl font-bold text-slate-950">Add company vehicle</h2>
                <div className="mt-4 space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <input placeholder="Registration" value={vehicleForm.registration} onChange={(event) => setVehicleForm({ ...vehicleForm, registration: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input placeholder="Fuel type" value={vehicleForm.fuelType} onChange={(event) => setVehicleForm({ ...vehicleForm, fuelType: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input placeholder="Make" value={vehicleForm.make} onChange={(event) => setVehicleForm({ ...vehicleForm, make: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input placeholder="Model" value={vehicleForm.model} onChange={(event) => setVehicleForm({ ...vehicleForm, model: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <select value={vehicleForm.assignedDriverId} onChange={(event) => setVehicleForm({ ...vehicleForm, assignedDriverId: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="">No driver assigned</option>
                    {carers.map((carer) => <option key={carer.id} value={carer.id}>{carer.fullName}</option>)}
                  </select>
                  <div className="grid gap-3 md:grid-cols-3">
                    <input type="date" title="Insurance expiry" value={vehicleForm.insuranceExpiry} onChange={(event) => setVehicleForm({ ...vehicleForm, insuranceExpiry: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input type="date" title="MOT expiry" value={vehicleForm.motExpiry} onChange={(event) => setVehicleForm({ ...vehicleForm, motExpiry: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                    <input type="date" title="Tax expiry" value={vehicleForm.taxExpiry} onChange={(event) => setVehicleForm({ ...vehicleForm, taxExpiry: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <button type="button" onClick={() => void saveVehicle()} disabled={isSaving} className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50 disabled:opacity-60">
                    <Plus className="h-4 w-4" />
                    Save vehicle
                  </button>
                </div>
              </section>
            )}
          </div>

          <div className="space-y-6">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-bold text-slate-950">Cost by vehicle</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {perVehicle.map(({ vehicle, fuelCost, litres, otherCost, total }) => (
                  <button key={vehicle.id} type="button" onClick={() => selectVehicleForExpense(vehicle.id || '')} className={`rounded-lg border p-4 text-left ${selectedVehicle?.id === vehicle.id ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50 hover:bg-purple-50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-slate-950">{vehicle.registration}</p>
                        <p className="mt-1 text-sm text-slate-600">{vehicle.make} {vehicle.model}</p>
                      </div>
                      <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-700">{vehicle.status}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <p className="text-slate-500">Fuel: <span className="font-semibold text-slate-900">{money(fuelCost)}</span></p>
                      <p className="text-slate-500">Litres: <span className="font-semibold text-slate-900">{litres.toFixed(1)}</span></p>
                      <p className="text-slate-500">Other: <span className="font-semibold text-slate-900">{money(otherCost)}</span></p>
                      <p className="text-slate-500">Total: <span className="font-semibold text-slate-900">{money(total)}</span></p>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">Driver: {vehicle.assignedDriverName || 'Not assigned'} | MOT: {vehicle.motExpiry || 'missing'}</p>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-950">Expense ledger</h2>
                  <p className="mt-1 text-sm text-slate-600">Selected vehicle: {selectedVehicle?.registration || 'All vehicles'}</p>
                </div>
                <p className="text-sm font-semibold text-slate-700">Selected cost: {money(selectedExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}</p>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Date</th>
                      <th className="px-3 py-3">Vehicle</th>
                      <th className="px-3 py-3">Driver</th>
                      <th className="px-3 py-3">Category</th>
                      <th className="px-3 py-3">Litres</th>
                      <th className="px-3 py-3">Amount</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(selectedVehicle ? selectedExpenses : expenses).map((expense) => (
                      <tr key={expense.id} className="align-top">
                        <td className="px-3 py-3 text-slate-700">{expense.date}</td>
                        <td className="px-3 py-3 font-semibold text-slate-950">{expense.vehicleRegistration}</td>
                        <td className="px-3 py-3 text-slate-700">{expense.driverName || 'Office'}</td>
                        <td className="px-3 py-3 text-slate-700">{expense.category}<br /><span className="text-xs text-slate-500">{expense.vendor}</span></td>
                        <td className="px-3 py-3 text-slate-700">{Number(expense.litres || 0).toFixed(1)}</td>
                        <td className="px-3 py-3 font-semibold text-slate-950">{money(Number(expense.amount || 0))}</td>
                        <td className="px-3 py-3">
                          {isAdminLike ? (
                            <select value={expense.status} onChange={(event) => void updateExpenseStatus(expense, event.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold">
                              <option>APPROVED</option>
                              <option>PENDING_REVIEW</option>
                              <option>REJECTED</option>
                            </select>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{expense.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

function MetricCard({ label, value, icon, alert = false }: { label: string; value: React.ReactNode; icon: React.ReactNode; alert?: boolean }) {
  return (
    <div className={`rounded-lg border p-5 shadow-sm ${alert ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className={`w-fit rounded-lg p-2 ${alert ? 'bg-amber-100 text-amber-700' : 'bg-purple-50 text-purple-700'}`}>{icon}</div>
      <p className="mt-4 text-2xl font-bold text-slate-950">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}
